import { Fragment } from 'react';

import useOmeMeetsDetail from './UseOmeMeetsDetail';

import ViewTemplate from '../../../../components/viewTemplate/ViewTemplate';
import Views from '../HostRegistrationViews';

import OmeMeetsDetailPricingAdditionalPurchasesGrid from './OmeMeetsDetailPricingAdditionalPurchasesGrid';

import Input from '@usas/ui-web/components/inputs/Input';
import DateTimePicker from '@usas/ui-web/components/datepickers/DateTimePicker';
import DatePicker from '@usas/ui-web/components/datepickers/DatePicker';
import OrgUnitTreeView from '@usas/ui-web/components/tree/orgUnitTree/OrgUnitTreeView';
import ReadOnly from '@usas/ui-web/components/readOnly/ReadOnly';
import PopUpModal from '@usas/ui-web/components/dialogs/PopUpModal';
import SecondaryButton from '@usas/ui-web/components/buttons/SecondaryButton';
import PrimaryButton from '@usas/ui-web/components/buttons/PrimaryButton';
import ToolTip from '@usas/ui-web/components/tooltips/ToolTip';
import YesNoSwitch from '@usas/ui-web/components/yesNoSwitch/YesNoSwitch';
import CurrencyInput from '@usas/ui-web/components/inputs/CurrencyInput';
import Headings from '@usas/ui-web/components/headings/Headings';
import ActionIntraPageButton from '@usas/ui-web/components/buttons/ActionIntraPageButton';

import Constants from '@usas/ui-web/utils/Constants';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';

import global from '@usas/ui-web/components/GlobalStyle.module.css';

const OmeMeetsDetail = () => {
  const {
    state,
    selectedOmeMeetEntryState,
    omeMeetState,
    omeMeetEntryState,
    omeMeetEntryInvitationsState,
    organizationInvitations,
    orgUnitState,
    dateTimeState,
    omeMeetDateValidationState,
    errorState,
    formState,
    handleSubmit,
    onFormValueChanged,
    onOrgUnitFormValueChanged,
    onNextButtonClicked,
    onBackButtonClicked,
    onAddAdditionalPurchaseItem,
    onEditAdditionalPurchaseItem,
    onDeleteAdditionalPurchaseItem,
    onDeleteAdditionalPurchaseItemClicked,
    modalState,
    onModalCanceled,
    allowAllEdits,
    allowSomeEdits
  } = useOmeMeetsDetail();

  return (
    <Fragment>
      <ViewTemplate
        viewName={Views.OME_MEET_OME_MEETS_DETAIL}
        onNextButtonClicked={onNextButtonClicked}
        onBackButtonClicked={onBackButtonClicked}
        errorMessage={errorState.error}>
        {selectedOmeMeetEntryState.initialLoadComplete === true &&
          <Fragment>
            {allowAllEdits() === true ?
              <Fragment>
                <form onSubmit={handleSubmit}>
                  <div className='row'>
                    <div className='col-xs-12'>
                      <Headings.H5>Meet Info</Headings.H5>
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <Input
                        label={<Fragment>OME Meet Name* <span><ToolTip label="When creating multiple OME Meets for one Meet, provide different OME Meet Names to distinguish between them"></ToolTip></span></Fragment>}
                        name="omeMeetName"
                        value={formState.omeMeetName}
                        error={errorState.omeMeetName}
                        message={errorState.omeMeetName}
                        onChange={(value) => { onFormValueChanged('omeMeetName', value) }} />
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <YesNoSwitch
                        label={<Fragment>Main OME Meet?* <span><ToolTip label="When creating multiple OME Meets for one Meet, one OME Meet serves as the main OME Meet."></ToolTip></span></Fragment>}
                        name="isMainEntry"
                        checked={formState.isMainEntry}
                        error={errorState.isMainEntry}
                        message={errorState.isMainEntry}
                        onChange={(value) => { onFormValueChanged('isMainEntry', value) }}
                        disabled={true} />
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-xs-12 col-sm-6'>
                      <DateTimePicker
                        label={<Fragment>Meet Entry Start Date* <span><ToolTip label="Date and time when teams can begin to submit their entries"></ToolTip></span></Fragment>}
                        name="meetEntryStartDate"
                        value={formState.meetEntryStartDate}
                        error={errorState.meetEntryStartDate}
                        message={errorState.meetEntryStartDate}
                        onChange={(value) => { onFormValueChanged('meetEntryStartDate', value); }}
                        countOfYears={9}
                        startYearOffset={-4} />
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <DateTimePicker
                        label={<Fragment>Meet Entry End Date* <span><ToolTip label="Deadline for teams to submit their entries"></ToolTip></span></Fragment>}
                        name="meetEntryEndDate"
                        value={formState.meetEntryEndDate}
                        error={errorState.meetEntryEndDate}
                        message={errorState.meetEntryEndDate}
                        onChange={(value) => { onFormValueChanged('meetEntryEndDate', value); }}
                        countOfYears={9}
                        startYearOffset={-4} />
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-xs-12 col-sm-6'>
                      <DateTimePicker
                        label={<Fragment>Meet Entry Change Deadline* <span><ToolTip label="Deadline for entered teams to request entry changes"></ToolTip></span></Fragment>}
                        name="meetEntryChangeDeadline"
                        value={formState.meetEntryChangeDeadline}
                        error={errorState.meetEntryChangeDeadline}
                        message={errorState.meetEntryChangeDeadline}
                        onChange={(value) => { onFormValueChanged('meetEntryChangeDeadline', value); }}
                        countOfYears={9}
                        startYearOffset={-4} />
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <ReadOnly
                        label={<Fragment>Meet Time Zone* <span><ToolTip label="Time Zone used for the meet"></ToolTip></span></Fragment>}
                        name="timeZoneValue"
                        value={omeMeetState.objData?.timeZone}
                      />
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-xs-12 usas-extra-top-margin'>
                      <div className='usas-extra-top-margin'>
                        <Headings.H5>Eligibility</Headings.H5>
                      </div>
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <Input
                        label={<Fragment>Max Number of Athletes For Meet<span>&nbsp;<ToolTip label={`Limit on the number of athletes in individual events (including any bonus events) that can enter the meet.`}></ToolTip></span></Fragment>}
                        name='maxNumberOfAthletesForMeet'
                        value={formState.maxNumberOfAthletesForMeet}
                        error={errorState.maxNumberOfAthletesForMeet}
                        message={errorState.maxNumberOfAthletesForMeet}
                        onChange={(value) => { onFormValueChanged('maxNumberOfAthletesForMeet', value); }} />
                    </div>
                    {formState.isMainEntry === false &&
                      <Fragment>
                        <div className='col-xs-12 col-sm-6'>
                          <YesNoSwitch
                            label='Count Max # of Athletes towards Main OME Meet Cap?'
                            name='maxNumberOfAthletesCountsTowardsMainOmeMeet'
                            checked={formState.maxNumberOfAthletesCountsTowardsMainOmeMeet}
                            error={errorState.maxNumberOfAthletesCountsTowardsMainOmeMeet}
                            message={errorState.maxNumberOfAthletesCountsTowardsMainOmeMeet}
                            onChange={(value) => { onFormValueChanged('maxNumberOfAthletesCountsTowardsMainOmeMeet', value); }}
                          />
                        </div>
                      </Fragment>}
                    {formState.relayEvents === true && formState.allowRelayOnlySwimmer === true &&
                      <Fragment>
                        <div className='col-xs-12 col-sm-6'>
                          <YesNoSwitch
                            label='Include Relay Only Swimmers in Max Number of Athletes?'
                            name='includeRelayOnlySwimmersInMaxNumber'
                            checked={formState.includeRelayOnlySwimmersInMaxNumber}
                            error={errorState.includeRelayOnlySwimmersInMaxNumber}
                            message={errorState.includeRelayOnlySwimmersInMaxNumber}
                            onChange={(value) => { onFormValueChanged('includeRelayOnlySwimmersInMaxNumber', value); }}
                          />
                        </div>
                      </Fragment>}
                    <div className='col-xs-12 usas-extra-top-margin'>
                      <div className='usas-extra-top-margin'>
                        <Headings.H5>Restrictions</Headings.H5>
                      </div>
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <YesNoSwitch
                        label='National Team Members only?*'
                        name='nationalTeamOnly'
                        checked={formState.nationalTeamOnly}
                        error={errorState.nationalTeamOnly}
                        message={errorState.nationalTeamOnly}
                        onChange={(value) => { onFormValueChanged('nationalTeamOnly', value); }} />
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-xs-12 usas-extra-top-margin'>
                      <div className='usas-extra-top-margin'>
                        <Headings.H5>Invitations</Headings.H5>
                      </div>
                    </div>
                    <div className='col-xs-12'>
                      <OrgUnitTreeView
                        label="Organization*"
                        name="orgUnit"
                        value={formState.orgUnit}
                        error={errorState.orgUnit}
                        message={errorState.orgUnit}
                        selectableLeavesOnly={false}
                        singleSelect={false}
                        onChange={(newValue) => { onOrgUnitFormValueChanged('orgUnit', newValue) }}
                        organizationId={undefined} />
                    </div>
                    <div className='col-xs-12'>
                      <ReadOnly
                        label="Selected Organizations/Teams"
                        value={organizationInvitations} />
                    </div>
                  </div>
                  <div className={formState.hasTimeStandards === true ? global.DisplayComponent : global.HideComponent} >
                    <div className='row'>
                      <div className='col-xs-12 usas-extra-top-margin'>
                        <div className='usas-extra-top-margin'>
                          <Headings.H5>Time Standards</Headings.H5>
                        </div>
                      </div>
                      <div className='col-xs-12'>
                        <YesNoSwitch
                          label="Time Standards use a Qualifying Window?*"
                          name="hasQualifyingWindow"
                          checked={formState.hasQualifyingWindow}
                          error={errorState.hasQualifyingWindow}
                          message={errorState.hasQualifyingWindow}
                          onChange={(value) => {
                            onFormValueChanged('hasQualifyingWindow', value);
                            if (value === true) {
                              onFormValueChanged('timeStandardsQualifyingStartDate', Constants.BLANK_DATE_STRING);
                              onFormValueChanged('timeStandardsQualifyingEndDate', Constants.BLANK_DATE_STRING);
                            }
                          }}
                        />
                      </div>
                      <div className={formState.hasQualifyingWindow === true ? global.DisplayComponent : global.HideComponent}>
                        <div className='col-xs-12 col-sm-6'>
                          <DatePicker
                            label={<Fragment>Qualifying Start Date* <span><ToolTip label="Qualifying swim times before this date are not accepted for the meet"></ToolTip></span></Fragment>}
                            name="timeStandardsQualifyingStartDate"
                            value={formState.timeStandardsQualifyingStartDate}
                            error={errorState.timeStandardsQualifyingStartDate}
                            message={errorState.timeStandardsQualifyingStartDate}
                            onChange={(value) => { onFormValueChanged('timeStandardsQualifyingStartDate', value); }}
                            countOfYears={155}
                            startYearOffset={-150} />
                        </div>
                        <div className='col-xs-12 col-sm-6'>
                          <DatePicker
                            label={<Fragment>Qualifying End Date* <span><ToolTip label="Qualifying swim times after this date are not accepted for the meet"></ToolTip></span></Fragment>}
                            name="timeStandardsQualifyingEndDate"
                            value={formState.timeStandardsQualifyingEndDate}
                            error={errorState.timeStandardsQualifyingEndDate}
                            message={errorState.timeStandardsQualifyingEndDate}
                            onChange={(value) => { onFormValueChanged('timeStandardsQualifyingEndDate', value); }}
                            countOfYears={155}
                            startYearOffset={-150} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <Fragment>
                    {allowSomeEdits() === true ?
                      <div className='row'>
                        <div className='col-xs-12 usas-extra-top-margin'>
                          <div className='usas-extra-top-margin'>
                            <Headings.H5>Pricing</Headings.H5>
                          </div>
                        </div>
                        <div className='col-xs-12 col-sm-6'>
                          <CurrencyInput
                            label="Cost Per Team*"
                            name="costPerTeam"
                            value={formState.costPerTeam}
                            error={errorState.costPerTeam}
                            message={errorState.costPerTeam}
                            onChange={(value) => { onFormValueChanged("costPerTeam", value); }}
                          />
                        </div>
                        <div className='col-xs-12 col-sm-6'>
                          <CurrencyInput
                            label={<Fragment>Cost Per Credential* <span><ToolTip label="Cost For Team Support Members: Coaches, Massage Therapists, Sports Medicine, etc."></ToolTip></span></Fragment>}
                            name="costPerCredential"
                            value={formState.costPerCredential}
                            error={errorState.costPerCredential}
                            message={errorState.costPerCredential}
                            onChange={(value) => { onFormValueChanged("costPerCredential", value); }}
                          />
                        </div>
                        <div className='col-xs-12 col-sm-6'>
                          <CurrencyInput
                            label="Cost Per Individual Athlete*"
                            name="costPerIndividualAthlete"
                            value={formState.costPerIndividualAthlete}
                            error={errorState.costPerIndividualAthlete}
                            message={errorState.costPerIndividualAthlete}
                            onChange={(value) => { onFormValueChanged("costPerIndividualAthlete", value); }}
                          />
                        </div>
                        <div className={formState.showCostPerIndividualInternationalAthlete === true ? global.DisplayComponent : global.HideComponent}>
                          <div className='col-xs-12 col-sm-6'>
                            <CurrencyInput
                              label="Cost Per Individual International Athlete*"
                              name="costPerIndividualInternationalAthlete"
                              value={formState.costPerIndividualInternationalAthlete}
                              error={errorState.costPerIndividualInternationalAthlete}
                              message={errorState.costPerIndividualInternationalAthlete}
                              onChange={(value) => { onFormValueChanged("costPerIndividualInternationalAthlete", value); }}
                            />
                          </div>
                        </div>
                        <div className={formState.showCostPerInternationalStaff === true ? global.DisplayComponent : global.HideComponent}>
                          <div className='col-xs-12 col-sm-6'>
                            <CurrencyInput
                              label="Cost Per International Staff*"
                              name="costPerInternationalStaff"
                              value={formState.costPerInternationalStaff}
                              error={errorState.costPerInternationalStaff}
                              message={errorState.costPerInternationalStaff}
                              onChange={(value) => { onFormValueChanged("costPerInternationalStaff", value); }}
                            />
                          </div>
                        </div>
                        <div className={formState.showCostPerRelayOnlySwimmer === true ? global.DisplayComponent : global.HideComponent}>
                          <div className='col-xs-12 col-sm-6'>
                            <CurrencyInput
                              label="Cost Per Relay Only Swimmer*"
                              name="costPerRelayOnlySwimmer"
                              value={formState.costPerRelayOnlySwimmer}
                              error={errorState.costPerRelayOnlySwimmer}
                              message={errorState.costPerRelayOnlySwimmer}
                              onChange={(value) => { onFormValueChanged("costPerRelayOnlySwimmer", value); }}
                            />
                          </div>
                        </div>
                        <div className='col-xs-12 col-sm-6'>
                          <CurrencyInput
                            label="Cost Per Athlete Individual Event Entry*"
                            name="costPerAthleteIndividualEventEntry"
                            value={formState.costPerAthleteIndividualEventEntry}
                            error={errorState.costPerAthleteIndividualEventEntry}
                            message={errorState.costPerAthleteIndividualEventEntry}
                            onChange={(value) => { onFormValueChanged("costPerAthleteIndividualEventEntry", value); }}
                          />
                        </div>
                        <div className={formState.showCostPerAthleteIndividualBonusEventEntry === true ? global.DisplayComponent : global.HideComponent}>
                          <div className='col-xs-12 col-sm-6'>
                            <CurrencyInput
                              label="Cost Per Athlete Individual Bonus Event Entry*"
                              name="costPerAthleteIndividualBonusEventEntry"
                              value={formState.costPerAthleteIndividualBonusEventEntry}
                              error={errorState.costPerAthleteIndividualBonusEventEntry}
                              message={errorState.costPerAthleteIndividualBonusEventEntry}
                              onChange={(value) => { onFormValueChanged("costPerAthleteIndividualBonusEventEntry", value); }}
                            />
                          </div>
                        </div>
                        <div className={formState.showCostPerRelayEventEntry === true ? global.DisplayComponent : global.HideComponent}>
                          <div className='col-xs-12 col-sm-6'>
                            <CurrencyInput
                              label="Cost Per Relay Event Entry*"
                              name="costPerRelayEventEntry"
                              value={formState.costPerRelayEventEntry}
                              error={errorState.costPerRelayEventEntry}
                              message={errorState.costPerRelayEventEntry}
                              onChange={(value) => { onFormValueChanged("costPerRelayEventEntry", value); }}
                            />
                          </div>
                        </div>
                      </div> :
                      <div className='row'>
                        <div className='col-xs-12 usas-extra-top-margin'>
                          <div className='usas-extra-top-margin'>
                            <Headings.H5>Pricing</Headings.H5>
                          </div>
                        </div>
                        <div className='col-xs-12 col-sm-6'>
                          {formState.currentCostPerTeam === '' ?
                            <CurrencyInput
                              label="Cost Per Team*"
                              name="costPerTeam"
                              value={formState.costPerTeam}
                              error={errorState.costPerTeam}
                              message={errorState.costPerTeam}
                              onChange={(value) => { onFormValueChanged("costPerTeam", value); }}
                            /> :
                            <ReadOnly
                              label="Cost Per Team"
                              name="costPerTeam"
                              value={`$${formState.costPerTeam !== '' && formState.costPerTeam >= 0 ? formState.costPerTeam.toFixed(2) : ''}`}
                            />
                          }
                        </div>
                        <div className='col-xs-12 col-sm-6'>
                          {formState.currentCostPerCredential === '' ?
                            <CurrencyInput
                              label={<Fragment>Cost Per Credential* <span><ToolTip label="Cost For Team Support Members: Coaches, Massage Therapists, Sports Medicine, etc."></ToolTip></span></Fragment>}
                              name="costPerCredential"
                              value={formState.costPerCredential}
                              error={errorState.costPerCredential}
                              message={errorState.costPerCredential}
                              onChange={(value) => { onFormValueChanged("costPerCredential", value); }}
                            />
                            :
                            <ReadOnly
                              label={<Fragment>Cost Per Credential <span><ToolTip label="Cost For Team Support Members: Coaches, Massage Therapists, Sports Medicine, etc."></ToolTip></span></Fragment>}
                              name="costPerCredential"
                              value={`$${formState.costPerCredential !== '' && formState.costPerCredential >= 0 ? formState.costPerCredential.toFixed(2) : ''}`}
                            />
                          }
                        </div>
                        <div className='col-xs-12 col-sm-6'>
                          {formState.currentCostPerIndividualAthlete === '' ?
                            <CurrencyInput
                              label="Cost Per Individual Athlete*"
                              name="costPerIndividualAthlete"
                              value={formState.costPerIndividualAthlete}
                              error={errorState.costPerIndividualAthlete}
                              message={errorState.costPerIndividualAthlete}
                              onChange={(value) => { onFormValueChanged("costPerIndividualAthlete", value); }}
                            /> :
                            <ReadOnly
                              label="Cost Per Individual Athlete"
                              name="costPerIndividualAthlete"
                              value={`$${formState.costPerIndividualAthlete !== '' && formState.costPerIndividualAthlete >= 0 ? formState.costPerIndividualAthlete.toFixed(2) : ''}`}
                            />}
                        </div>
                        <div className={formState.showCostPerIndividualInternationalAthlete === true ? global.DisplayComponent : global.HideComponent}>
                          <div className='col-xs-12 col-sm-6'>
                            {formState.currentCostPerIndividualInternationalAthlete === '' ?
                              <CurrencyInput
                                label="Cost Per Individual International Athlete*"
                                name="costPerIndividualInternationalAthlete"
                                value={formState.costPerIndividualInternationalAthlete}
                                error={errorState.costPerIndividualInternationalAthlete}
                                message={errorState.costPerIndividualInternationalAthlete}
                                onChange={(value) => { onFormValueChanged("costPerIndividualInternationalAthlete", value); }}
                              /> :
                              <ReadOnly
                                label="Cost Per Individual International Athlete"
                                name="costPerIndividualInternationalAthlete"
                                value={`$${formState.costPerIndividualInternationalAthlete !== '' && formState.costPerIndividualInternationalAthlete >= 0 ? formState.costPerIndividualInternationalAthlete.toFixed(2) : ''}`}
                              />}
                          </div>
                        </div>
                        <div className={formState.showCostPerInternationalStaff === true ? global.DisplayComponent : global.HideComponent}>
                          <div className='col-xs-12 col-sm-6'>
                            {formState.currentCostPerInternationalStaff === '' ?
                              <CurrencyInput
                                label="Cost Per International Staff*"
                                name="costPerInternationalStaff"
                                value={formState.costPerInternationalStaff}
                                error={errorState.costPerInternationalStaff}
                                message={errorState.costPerInternationalStaff}
                                onChange={(value) => { onFormValueChanged("costPerInternationalStaff", value); }}
                              /> :
                              <ReadOnly
                                label="Cost Per International Staff"
                                name="costPerInternationalStaff"
                                value={`$${formState.costPerInternationalStaff !== '' && formState.costPerInternationalStaff >= 0 ? formState.costPerInternationalStaff.toFixed(2) : ''}`}
                              />}
                          </div>
                        </div>
                        <div className={formState.showCostPerRelayOnlySwimmer === true ? global.DisplayComponent : global.HideComponent}>
                          <div className='col-xs-12 col-sm-6'>
                            {formState.currentCostPerRelayOnlySwimmer === '' ?
                              <CurrencyInput
                                label="Cost Per Relay Only Swimmer*"
                                name="costPerRelayOnlySwimmer"
                                value={formState.costPerRelayOnlySwimmer}
                                error={errorState.costPerRelayOnlySwimmer}
                                message={errorState.costPerRelayOnlySwimmer}
                                onChange={(value) => { onFormValueChanged("costPerRelayOnlySwimmer", value); }}
                              /> :
                              <ReadOnly
                                label="Cost Per Relay Only Swimmer"
                                name="costPerRelayOnlySwimmer"
                                value={`$${formState.costPerRelayOnlySwimmer !== '' && formState.costPerRelayOnlySwimmer >= 0 ? formState.costPerRelayOnlySwimmer.toFixed(2) : ''}`}
                              />}
                          </div>
                        </div>
                        <div className='col-xs-12 col-sm-6'>
                          {formState.currentCostPerAthleteIndividualEventEntry === '' ?
                            <CurrencyInput
                              label="Cost Per Athlete Individual Event Entry*"
                              name="costPerAthleteIndividualEventEntry"
                              value={formState.costPerAthleteIndividualEventEntry}
                              error={errorState.costPerAthleteIndividualEventEntry}
                              message={errorState.costPerAthleteIndividualEventEntry}
                              onChange={(value) => { onFormValueChanged("costPerAthleteIndividualEventEntry", value); }}
                            /> :
                            <ReadOnly
                              label="Cost Per Athlete Individual Event Entry*"
                              name="costPerAthleteIndividualEventEntry"
                              value={`$${formState.costPerAthleteIndividualEventEntry !== '' && formState.costPerAthleteIndividualEventEntry >= 0 ? formState.costPerAthleteIndividualEventEntry.toFixed(2) : ''}`}
                            />}
                        </div>
                        <div className={formState.showCostPerAthleteIndividualBonusEventEntry === true ? global.DisplayComponent : global.HideComponent}>
                          <div className='col-xs-12 col-sm-6'>
                            {formState.currentCostPerAthleteIndividualBonusEventEntry === '' ?
                              <CurrencyInput
                                label="Cost Per Athlete Individual Bonus Event Entry*"
                                name="costPerAthleteIndividualBonusEventEntry"
                                value={formState.costPerAthleteIndividualBonusEventEntry}
                                error={errorState.costPerAthleteIndividualBonusEventEntry}
                                message={errorState.costPerAthleteIndividualBonusEventEntry}
                                onChange={(value) => { onFormValueChanged("costPerAthleteIndividualBonusEventEntry", value); }}
                              /> :
                              <ReadOnly
                                label="Cost Per Athlete Individual Bonus Event Entry"
                                name="costPerAthleteIndividualBonusEventEntry"
                                value={`$${formState.costPerAthleteIndividualBonusEventEntry !== '' && formState.costPerAthleteIndividualBonusEventEntry >= 0 ? formState.costPerAthleteIndividualBonusEventEntry.toFixed(2) : ''}`}
                              />}
                          </div>
                        </div>
                        <div className={formState.showCostPerRelayEventEntry === true ? global.DisplayComponent : global.HideComponent}>
                          <div className='col-xs-12 col-sm-6'>
                            {formState.currentCostPerRelayEventEntry === '' ?
                              <CurrencyInput
                                label="Cost Per Relay Event Entry*"
                                name="costPerRelayEventEntry"
                                value={formState.costPerRelayEventEntry}
                                error={errorState.costPerRelayEventEntry}
                                message={errorState.costPerRelayEventEntry}
                                onChange={(value) => { onFormValueChanged("costPerRelayEventEntry", value); }}
                              /> :
                              <ReadOnly
                                label="Cost Per Relay Event Entry"
                                name="costPerRelayEventEntry"
                                value={`$${formState.costPerRelayEventEntry !== '' && formState.costPerRelayEventEntry >= 0 ? formState.costPerRelayEventEntry.toFixed(2) : ''}`}
                              />}
                          </div>
                        </div>
                      </div>}
                  </Fragment>
                </form>
                <Fragment>
                  {allowSomeEdits() === true ?
                    <div className='row'>
                      <div className="col-xs-12 usas-extra-top-margin">
                        <ActionIntraPageButton type="button" onClick={onAddAdditionalPurchaseItem}>Add Additional Purchase Item</ActionIntraPageButton>
                      </div>
                      <div className="col-xs-12">
                        <OmeMeetsDetailPricingAdditionalPurchasesGrid
                          selectedOmeMeetEntryState={selectedOmeMeetEntryState}
                          omeMeetEntryState={omeMeetEntryState}
                          gridState={state}
                          onEdit={onEditAdditionalPurchaseItem}
                          onDelete={onDeleteAdditionalPurchaseItemClicked}
                        />
                      </div>
                    </div> :
                    <div className='row'>
                      {allowAllEdits() === true ?
                        <div className="col-xs-12 usas-extra-top-margin">
                          <ActionIntraPageButton type="button" onClick={onAddAdditionalPurchaseItem}>Add Additional Purchase Item</ActionIntraPageButton>
                        </div> :
                        <div className='col-xs-12 usas-extra-top-margin'>
                          <Headings.H6>Additional Purchase Items</Headings.H6>
                        </div>}
                      <div className="col-xs-12">
                        <OmeMeetsDetailPricingAdditionalPurchasesGrid
                          selectedOmeMeetEntryState={selectedOmeMeetEntryState}
                          omeMeetEntryState={omeMeetEntryState}
                          gridState={state}
                        />
                      </div>
                    </div>}
                </Fragment>
              </Fragment> :
              <Fragment>
                <form onSubmit={handleSubmit}>
                  <div className='row'>
                    <div className='col-xs-12'>
                      <Headings.H5>Meet Info</Headings.H5>
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <ReadOnly
                        label={<Fragment>OME Meet Name* <span><ToolTip label="When creating multiple OME Meets for one Meet, provide different OME Meet Names to distinguish between them"></ToolTip></span></Fragment>}
                        name="omeMeetName"
                        value={formState.omeMeetName}
                      />
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <YesNoSwitch
                        label={<Fragment>Main OME Meet?* <span><ToolTip label="When creating multiple OME Meets for one Meet, one OME Meet serves as the main OME Meet."></ToolTip></span></Fragment>}
                        name="isMainEntry"
                        checked={formState.isMainEntry}
                        error={errorState.isMainEntry}
                        message={errorState.isMainEntry}
                        onChange={(value) => { onFormValueChanged('isMainEntry', value) }}
                        disabled={true} />
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <ReadOnly
                        label={<Fragment>Meet Entry Start Date* <span><ToolTip label="Date and time when teams can begin to submit their entries"></ToolTip></span></Fragment>}
                        name="meetEntryStartDate"
                        value={formState.meetEntryStartDateTwelveHourClock}
                      />
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <ReadOnly
                        label={<Fragment>Meet Entry End Date* <span><ToolTip label="Deadline for teams to submit their entries"></ToolTip></span></Fragment>}
                        name="meetEntryEndDate"
                        value={formState.meetEntryEndDateTwelveHourClock}
                      />
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <ReadOnly
                        label={<Fragment>Meet Entry Change Deadline* <span><ToolTip label="Deadline for entered teams to request entry changes"></ToolTip></span></Fragment>}
                        name="meetEntryChangeDeadline"
                        value={formState.meetEntryChangeDeadlineTwelveHourClock}
                      />
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <ReadOnly
                        label={<Fragment>Meet Time Zone* <span><ToolTip label="Time Zone used for the meet"></ToolTip></span></Fragment>}
                        name="timeZoneValue"
                        value={omeMeetState.objData?.timeZone}
                      />
                    </div>
                    <div className='col-xs-12 usas-extra-top-margin'>
                      <div className='usas-extra-top-margin'>
                        <Headings.H5>Eligibility</Headings.H5>
                      </div>
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <ReadOnly
                        label={<Fragment>Max Number of Athletes For Meet<span>&nbsp;<ToolTip label={`Limit on the number of athletes in individual events (including any bonus events) that can enter the meet.`}></ToolTip></span></Fragment>}
                        name='maxNumberOfAthletesForMeet'
                        value={formState.maxNumberOfAthletesForMeet}
                      />
                    </div>
                    {formState.isMainEntry === false &&
                      <Fragment>
                        <div className='col-xs-12 col-sm-6'>
                          <YesNoSwitch
                            label='Count Max # of Athletes towards Main OME Meet Cap?'
                            name='maxNumberOfAthletesCountsTowardsMainOmeMeet'
                            checked={formState.maxNumberOfAthletesCountsTowardsMainOmeMeet}
                            error={errorState.maxNumberOfAthletesCountsTowardsMainOmeMeet}
                            message={errorState.maxNumberOfAthletesCountsTowardsMainOmeMeet}
                            onChange={(value) => { onFormValueChanged('maxNumberOfAthletesCountsTowardsMainOmeMeet', value); }}
                            disabled={true}
                          />
                        </div>
                      </Fragment>}
                    {formState.relayEvents === true && formState.allowRelayOnlySwimmer === true &&
                      <Fragment>
                        <div className='col-xs-12 col-sm-6'>
                          <YesNoSwitch
                            label='Include Relay Only Swimmers in Max Number of Athletes?'
                            name='includeRelayOnlySwimmersInMaxNumber'
                            checked={formState.includeRelayOnlySwimmersInMaxNumber}
                            error={errorState.includeRelayOnlySwimmersInMaxNumber}
                            message={errorState.includeRelayOnlySwimmersInMaxNumber}
                            onChange={(value) => { onFormValueChanged('includeRelayOnlySwimmersInMaxNumber', value); }}
                            disabled={true} />
                        </div>
                      </Fragment>}
                    <div className='col-xs-12 usas-extra-top-margin'>
                      <div className='usas-extra-top-margin'>
                        <Headings.H5>Restrictions</Headings.H5>
                      </div>
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      <YesNoSwitch
                        label='National Team Members only?*'
                        name='nationalTeamOnly'
                        checked={formState.nationalTeamOnly}
                        error={errorState.nationalTeamOnly}
                        message={errorState.nationalTeamOnly}
                        onChange={(value) => { onFormValueChanged('nationalTeamOnly', value); }}
                        disabled={true} />
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-xs-12 usas-extra-top-margin'>
                      <div className='usas-extra-top-margin'>
                        <Headings.H5>Invitations</Headings.H5>
                      </div>
                    </div>
                    <div className='col-xs-12'>
                      <ReadOnly
                        label="Selected Organizations/Teams"
                        value={organizationInvitations} />
                    </div>
                  </div>
                  <div className={formState.hasTimeStandards === true ? global.DisplayComponent : global.HideComponent} >
                    <div className='row'>
                      <div className='col-xs-12 usas-extra-top-margin'>
                        <div className='usas-extra-top-margin'>
                          <Headings.H5>Time Standards</Headings.H5>
                        </div>
                      </div>
                      <div className='col-xs-12'>
                        <YesNoSwitch
                          label="Time Standards use a Qualifying Window?*"
                          name="hasQualifyingWindow"
                          checked={formState.hasQualifyingWindow}
                          error={errorState.hasQualifyingWindow}
                          message={errorState.hasQualifyingWindow}
                          onChange={(value) => { onFormValueChanged('hasQualifyingWindow', value); }}
                          disabled={true}
                        />
                      </div>
                      <div className={formState.hasQualifyingWindow === true ? global.DisplayComponent : global.HideComponent}>
                        <div className='col-xs-12 col-sm-6'>
                          <ReadOnly
                            label={<Fragment>Qualifying Start Date* <span><ToolTip label="Qualifying swim times before this date are not accepted for the meet"></ToolTip></span></Fragment>}
                            name="timeStandardsQualifyingStartDate"
                            value={formatDate(formState.timeStandardsQualifyingStartDate)} />
                        </div>
                        <div className='col-xs-12 col-sm-6'>
                          <ReadOnly
                            label={<Fragment>Qualifying End Date* <span><ToolTip label="Qualifying swim times after this date are not accepted for the meet"></ToolTip></span></Fragment>}
                            name="timeStandardsQualifyingEndDate"
                            value={formatDate(formState.timeStandardsQualifyingEndDate)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-xs-12 usas-extra-top-margin'>
                      <div className='usas-extra-top-margin'>
                        <Headings.H5>Pricing</Headings.H5>
                      </div>
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      {formState.currentCostPerTeam === '' ?
                        <CurrencyInput
                          label="Cost Per Team*"
                          name="costPerTeam"
                          value={formState.costPerTeam}
                          error={errorState.costPerTeam}
                          message={errorState.costPerTeam}
                          onChange={(value) => { onFormValueChanged("costPerTeam", value); }}
                        /> :
                        <ReadOnly
                          label="Cost Per Team"
                          name="costPerTeam"
                          value={`$${formState.costPerTeam !== '' && formState.costPerTeam >= 0 ? formState.costPerTeam.toFixed(2) : ''}`}
                        />
                      }
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      {formState.currentCostPerCredential === '' ?
                        <CurrencyInput
                          label={<Fragment>Cost Per Credential* <span><ToolTip label="Cost For Team Support Members: Coaches, Massage Therapists, Sports Medicine, etc."></ToolTip></span></Fragment>}
                          name="costPerCredential"
                          value={formState.costPerCredential}
                          error={errorState.costPerCredential}
                          message={errorState.costPerCredential}
                          onChange={(value) => { onFormValueChanged("costPerCredential", value); }}
                        />
                        :
                        <ReadOnly
                          label={<Fragment>Cost Per Credential <span><ToolTip label="Cost For Team Support Members: Coaches, Massage Therapists, Sports Medicine, etc."></ToolTip></span></Fragment>}
                          name="costPerCredential"
                          value={`$${formState.costPerCredential !== '' && formState.costPerCredential >= 0 ? formState.costPerCredential.toFixed(2) : ''}`}
                        />
                      }
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      {formState.currentCostPerIndividualAthlete === '' ?
                        <CurrencyInput
                          label="Cost Per Individual Athlete*"
                          name="costPerIndividualAthlete"
                          value={formState.costPerIndividualAthlete}
                          error={errorState.costPerIndividualAthlete}
                          message={errorState.costPerIndividualAthlete}
                          onChange={(value) => { onFormValueChanged("costPerIndividualAthlete", value); }}
                        /> :
                        <ReadOnly
                          label="Cost Per Individual Athlete"
                          name="costPerIndividualAthlete"
                          value={`$${formState.costPerIndividualAthlete !== '' && formState.costPerIndividualAthlete >= 0 ? formState.costPerIndividualAthlete.toFixed(2) : ''}`}
                        />}
                    </div>
                    <div className={formState.showCostPerIndividualInternationalAthlete === true ? global.DisplayComponent : global.HideComponent}>
                      <div className='col-xs-12 col-sm-6'>
                        {formState.currentCostPerIndividualInternationalAthlete === '' ?
                          <CurrencyInput
                            label="Cost Per Individual International Athlete*"
                            name="costPerIndividualInternationalAthlete"
                            value={formState.costPerIndividualInternationalAthlete}
                            error={errorState.costPerIndividualInternationalAthlete}
                            message={errorState.costPerIndividualInternationalAthlete}
                            onChange={(value) => { onFormValueChanged("costPerIndividualInternationalAthlete", value); }}
                          /> :
                          <ReadOnly
                            label="Cost Per Individual International Athlete"
                            name="costPerIndividualInternationalAthlete"
                            value={`$${formState.costPerIndividualInternationalAthlete !== '' && formState.costPerIndividualInternationalAthlete >= 0 ? formState.costPerIndividualInternationalAthlete.toFixed(2) : ''}`}
                          />}
                      </div>
                    </div>
                    <div className={formState.showCostPerInternationalStaff === true ? global.DisplayComponent : global.HideComponent}>
                      <div className='col-xs-12 col-sm-6'>
                        {formState.currentCostPerInternationalStaff === '' ?
                          <CurrencyInput
                            label="Cost Per International Staff*"
                            name="costPerInternationalStaff"
                            value={formState.costPerInternationalStaff}
                            error={errorState.costPerInternationalStaff}
                            message={errorState.costPerInternationalStaff}
                            onChange={(value) => { onFormValueChanged("costPerInternationalStaff", value); }}
                          /> :
                          <ReadOnly
                            label="Cost Per International Staff"
                            name="costPerInternationalStaff"
                            value={`$${formState.costPerInternationalStaff !== '' && formState.costPerInternationalStaff >= 0 ? formState.costPerInternationalStaff.toFixed(2) : ''}`}
                          />}
                      </div>
                    </div>
                    <div className={formState.showCostPerRelayOnlySwimmer === true ? global.DisplayComponent : global.HideComponent}>
                      <div className='col-xs-12 col-sm-6'>
                        {formState.currentCostPerRelayOnlySwimmer === '' ?
                          <CurrencyInput
                            label="Cost Per Relay Only Swimmer*"
                            name="costPerRelayOnlySwimmer"
                            value={formState.costPerRelayOnlySwimmer}
                            error={errorState.costPerRelayOnlySwimmer}
                            message={errorState.costPerRelayOnlySwimmer}
                            onChange={(value) => { onFormValueChanged("costPerRelayOnlySwimmer", value); }}
                          /> :
                          <ReadOnly
                            label="Cost Per Relay Only Swimmer"
                            name="costPerRelayOnlySwimmer"
                            value={`$${formState.costPerRelayOnlySwimmer !== '' && formState.costPerRelayOnlySwimmer >= 0 ? formState.costPerRelayOnlySwimmer.toFixed(2) : ''}`}
                          />}
                      </div>
                    </div>
                    <div className='col-xs-12 col-sm-6'>
                      {formState.currentCostPerAthleteIndividualEventEntry === '' ?
                        <CurrencyInput
                          label="Cost Per Athlete Individual Event Entry*"
                          name="costPerAthleteIndividualEventEntry"
                          value={formState.costPerAthleteIndividualEventEntry}
                          error={errorState.costPerAthleteIndividualEventEntry}
                          message={errorState.costPerAthleteIndividualEventEntry}
                          onChange={(value) => { onFormValueChanged("costPerAthleteIndividualEventEntry", value); }}
                        /> :
                        <ReadOnly
                          label="Cost Per Athlete Individual Event Entry*"
                          name="costPerAthleteIndividualEventEntry"
                          value={`$${formState.costPerAthleteIndividualEventEntry !== '' && formState.costPerAthleteIndividualEventEntry >= 0 ? formState.costPerAthleteIndividualEventEntry.toFixed(2) : ''}`}
                        />}
                    </div>
                    <div className={formState.showCostPerAthleteIndividualBonusEventEntry === true ? global.DisplayComponent : global.HideComponent}>
                      <div className='col-xs-12 col-sm-6'>
                        {formState.currentCostPerAthleteIndividualBonusEventEntry === '' ?
                          <CurrencyInput
                            label="Cost Per Athlete Individual Bonus Event Entry*"
                            name="costPerAthleteIndividualBonusEventEntry"
                            value={formState.costPerAthleteIndividualBonusEventEntry}
                            error={errorState.costPerAthleteIndividualBonusEventEntry}
                            message={errorState.costPerAthleteIndividualBonusEventEntry}
                            onChange={(value) => { onFormValueChanged("costPerAthleteIndividualBonusEventEntry", value); }}
                          /> :
                          <ReadOnly
                            label="Cost Per Athlete Individual Bonus Event Entry"
                            name="costPerAthleteIndividualBonusEventEntry"
                            value={`$${formState.costPerAthleteIndividualBonusEventEntry !== '' && formState.costPerAthleteIndividualBonusEventEntry >= 0 ? formState.costPerAthleteIndividualBonusEventEntry.toFixed(2) : ''}`}
                          />}
                      </div>
                    </div>
                    <div className={formState.showCostPerRelayEventEntry === true ? global.DisplayComponent : global.HideComponent}>
                      <div className='col-xs-12 col-sm-6'>
                        {formState.currentCostPerRelayEventEntry === '' ?
                          <CurrencyInput
                            label="Cost Per Relay Event Entry*"
                            name="costPerRelayEventEntry"
                            value={formState.costPerRelayEventEntry}
                            error={errorState.costPerRelayEventEntry}
                            message={errorState.costPerRelayEventEntry}
                            onChange={(value) => { onFormValueChanged("costPerRelayEventEntry", value); }}
                          /> :
                          <ReadOnly
                            label="Cost Per Relay Event Entry"
                            name="costPerRelayEventEntry"
                            value={`$${formState.costPerRelayEventEntry !== '' && formState.costPerRelayEventEntry >= 0 ? formState.costPerRelayEventEntry.toFixed(2) : ''}`}
                          />}
                      </div>
                    </div>
                  </div>
                </form>
                <div className='row'>
                  <div className='col-xs-12 usas-extra-top-margin'>
                    <Headings.H6>Additional Purchase Items</Headings.H6>
                  </div>
                  <div className="col-xs-12">
                    <OmeMeetsDetailPricingAdditionalPurchasesGrid
                      selectedOmeMeetEntryState={selectedOmeMeetEntryState}
                      omeMeetEntryState={omeMeetEntryState}
                      gridState={state}
                    />
                  </div>
                </div>
              </Fragment>
            }
            <PopUpModal
              widthPct={90}
              maxWidth={720}
              title={modalState.modalTitle}
              displayPopUp={modalState.displayPopUp}
              onModalCanceled={onModalCanceled}>
              <div className="row">
                <div className="col-xs-12">
                  <p><b>Fee Type:</b>&nbsp;{modalState.omeFeeTypeName}</p>
                  <p><b>Cost:</b>&nbsp;{modalState.amount}</p>
                  <p><b>Description:</b>&nbsp;{modalState.description}</p>
                </div>
                <div className="col-xs-12 usas-extra-top-margin">
                  <SecondaryButton type="button" onClick={onModalCanceled}>Cancel</SecondaryButton>&nbsp;
                  <PrimaryButton type="button" onClick={(e) => onDeleteAdditionalPurchaseItem(e)}>Delete</PrimaryButton>
                </div>
              </div>
            </PopUpModal>
            <PopUpModal
              widthPct={50}
              maxWidth={250}
              title={Constants.LOADING_MSG}
              displayPopUp={orgUnitState.isArrayLoading || omeMeetEntryState.isObjLoading || dateTimeState.isObjLoading || omeMeetDateValidationState.isObjLoading} />
            <PopUpModal
              widthPct={50}
              maxWidth={250}
              title={Constants.SAVING_MSG}
              displayPopUp={omeMeetEntryState.isSaving || omeMeetEntryInvitationsState.isSaving}
            />
          </Fragment>}
      </ViewTemplate>
    </Fragment>
  );
};

export default OmeMeetsDetail;